import { useState, useEffect } from "react"
import { X, ArrowLeft, ArrowRight, Brain, AlertCircle, CheckCircle, Info } from "lucide-react"
import authService from "../../../Services/authService";
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const OPENAI_API_KEY = "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"; // Replace with secure handling in production


export function LabReportComparison({ isOpen, onClose, selectedReports }) {
  const [analyzing, setAnalyzing] = useState(true)
  const [progress, setProgress] = useState(0)
  const [comparisonResult, setComparisonResult] = useState(null)
  const [activeTab, setActiveTab] = useState("summary")

  //console.log("LabReportComparison rendered with:", { isOpen, selectedReports }) // Debug log
  console.log("LabReportComparison rendered with:", { selectedReports }) // Debug log
  // Mock data for comparison results
  const mockComparisonResults = {
    summary:
      "Comparison shows improvement in cholesterol levels and white blood cell count between the two tests. Hemoglobin levels have remained stable.",
    changes: [
      {
        parameter: "White Blood Cells",
        firstValue: "11.5",
        firstUnit: "x10^9/L",
        firstStatus: "high",
        secondValue: "9.8",
        secondUnit: "x10^9/L",
        secondStatus: "normal",
        change: "decrease",
        significance: "positive",
        notes: "White blood cell count has decreased to normal range, indicating reduced inflammation.",
      },
      {
        parameter: "Hemoglobin",
        firstValue: "14.2",
        firstUnit: "g/dL",
        firstStatus: "normal",
        secondValue: "14.5",
        secondUnit: "g/dL",
        secondStatus: "normal",
        change: "stable",
        significance: "neutral",
        notes: "Hemoglobin levels remain stable within the normal range.",
      },
      {
        parameter: "Cholesterol",
        firstValue: "240",
        firstUnit: "mg/dL",
        firstStatus: "high",
        secondValue: "210",
        secondUnit: "mg/dL",
        secondStatus: "high",
        change: "decrease",
        significance: "positive",
        notes: "Total cholesterol has decreased but remains above the recommended range.",
      },
      {
        parameter: "Glucose",
        firstValue: "95",
        firstUnit: "mg/dL",
        firstStatus: "normal",
        secondValue: "105",
        secondUnit: "mg/dL",
        secondStatus: "elevated",
        change: "increase",
        significance: "negative",
        notes: "Glucose levels have increased and are now slightly elevated.",
      },
    ],
    recommendations: [
      "Continue with current cholesterol management approach as it shows positive results",
      "Monitor glucose levels more closely as they show an upward trend",
      "Schedule a follow-up test in 3 months to track progress",
    ],
  }

  const startComparison = async () => {
    if (selectedReports.length !== 2) return;

    const [reportA, reportB] = selectedReports;
    const urlA = `${authService.getApiDomainUrl()}${reportA.document_name}`;
    const urlB = `${authService.getApiDomainUrl()}${reportB.document_name}`;

    console.log("Comparing PDFs:", urlA, urlB);

    try {
      const textA = await extractTextFromPDF(urlA);
      const textB = await extractTextFromPDF(urlB);

      console.log("Extracted Text A:", textA.slice(0, 300));
      console.log("Extracted Text B:", textB.slice(0, 300));

      const comparison = await compareWithGPT(textA, textB);
      console.log("GPT Comparison Result venky:", comparison);
      setAnalyzing(false)
      console.log("Setting comparison result") // Debug log
      setComparisonResult(comparison)
      //alert("Comparison Result:\n" + comparison);
    } catch (error) {
      console.error("Comparison error:", error);
      alert("Failed to compare reports.");
    }
  };

  const extractTextFromPDF = async (originalUrl) => {
    const proxyUrl = `https://api.Pranaam365.com/api/proxy-pdf?url=${encodeURIComponent(originalUrl)}`;
    const loadingTask = pdfjsLib.getDocument(proxyUrl);
    const pdf = await loadingTask.promise;

    let fullText = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const textItems = textContent.items.map(item => item.str);
      fullText += textItems.join(" ") + "\n";
    }
    return fullText;
  };

  const compareWithGPT = async (text1, text2) => {
    const messages = [
      {
        role: "system",
        content:
          "You are a medical assistant. Compare two lab test reports and return the result in structured JSON format for easy display in a UI."
      },
      {
        role: "user",
        content: `Compare the following two lab reports:\n\nReport A:\n${text1}\n\nReport B:\n${text2}\n\nReturn the response in this exact JSON format:\n\n{
  "summary": "Brief comparison summary",
  "changes": [
    {
      "parameter": "Name of the lab parameter",
      "firstValue": "Value in first report",
      "firstUnit": "Unit",
      "firstStatus": "normal|high|low|elevated|critical",
      "secondValue": "Value in second report",
      "secondUnit": "Unit",
      "secondStatus": "normal|high|low|elevated|critical",
      "change": "increase|decrease|stable",
      "significance": "positive|negative|neutral",
      "notes": "Reason or interpretation of this change"
    }
  ],
  "recommendations": [
    "Recommendation 1",
    "Recommendation 2"
  ]
}`
      }
    ];

    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4",
          messages,
          temperature: 0.3,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || "API request failed.");
      }

      const content = result.choices?.[0]?.message?.content;

      try {
        const parsed = JSON.parse(content);
        return parsed;
      } catch (jsonErr) {
        console.error("Failed to parse JSON from GPT response:", jsonErr);
        throw new Error("Invalid response format. Please try again.");
      }
    } catch (error) {
      console.error("Error comparing reports:", error);
      return {
        summary: "Comparison failed.",
        changes: [],
        recommendations: []
      };
    }
  };




  // const compareWithGPT = async (text1, text2) => {
  //   const messages = [
  //     { role: "system", content: "Compare the following two medical reports and highlight key differences in lab values, tests, or findings." },
  //     { role: "user", content: `Report A:\n${text1}` },
  //     { role: "user", content: `Report B:\n${text2}` },
  //   ];

  //   const response = await fetch("https://api.openai.com/v1/chat/completions", {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //       Authorization: `Bearer ${OPENAI_API_KEY}`,
  //     },
  //     body: JSON.stringify({
  //       model: "gpt-4",
  //       messages,
  //       temperature: 0.3,
  //     }),
  //   });

  //   const result = await response.json();
  //   return result.choices?.[0]?.message?.content || "Comparison failed.";
  // };

  // Simulate analysis process
  // useEffect(() => {
  //   if (isOpen) {
  //     console.log("Comparison modal opened, starting analysis simulation") // Debug log
  //     setAnalyzing(true)
  //     setProgress(0)
  //     //setComparisonResult(null)

  //     const interval = setInterval(() => {
  //       setProgress((prev) => {
  //         const newProgress = prev + 10
  //         if (newProgress >= 100) {
  //           clearInterval(interval)
  //           setTimeout(() => {
  //             setAnalyzing(false)
  //             console.log("Setting comparison result") // Debug log
  //             //setComparisonResult(mockComparisonResults)
  //           }, 500)
  //           return 100
  //         }
  //         return newProgress
  //       })
  //     }, 300)

  //     return () => clearInterval(interval)
  //   }
  // }, [isOpen])

  useEffect(() => {
    if (!isOpen) return;

    setAnalyzing(true);
    setProgress(0);
    setComparisonResult(null);

    // Simulated progress logic
    let progressValue = 0;
    const interval = setInterval(() => {
      progressValue += Math.floor(Math.random() * 10) + 5; // increment by 5-14
      setProgress(prev => (progressValue < 95 ? progressValue : 95)); // cap at 95% until completion
    }, 300);

    startComparison().finally(() => {
      clearInterval(interval); // stop the interval once done
      setProgress(100); // ensure full bar at the end
      setAnalyzing(false);
    });

    return () => clearInterval(interval);
  }, [isOpen]);



  const getStatusIcon = (status) => {
    switch (status) {
      case "normal":
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case "high":
        return <AlertCircle className="h-4 w-4 text-amber-500" />
      case "low":
        return <AlertCircle className="h-4 w-4 text-blue-500" />
      case "critical":
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case "elevated":
        return <AlertCircle className="h-4 w-4 text-orange-500" />
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getChangeIcon = (change, significance) => {
    if (change === "increase") {
      return (
        <ArrowRight
          className={`h-4 w-4 ${significance === "positive" ? "text-green-500" : significance === "negative" ? "text-red-500" : "text-blue-500"}`}
        />
      )
    } else if (change === "decrease") {
      return (
        <ArrowLeft
          className={`h-4 w-4 ${significance === "positive" ? "text-green-500" : significance === "negative" ? "text-red-500" : "text-blue-500"}`}
        />
      )
    } else {
      return <span className="h-4 w-4 inline-block text-center">—</span>
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="dialog-content sm:max-w-[700px]">
        <div className="dialog-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Lab Report Comparison</h3>
            </div>
            <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* <p className="text-sm text-muted-foreground">
            Comparing: {selectedReports[0]?.title} ({selectedReports[0]?.date}) and {selectedReports[1]?.title} (
            {selectedReports[1]?.date})
          </p> */}
        </div>
        {analyzing ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <div className="h-16 w-16 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium">Analyzing and comparing lab reports...</h3>
              <p className="text-sm text-muted-foreground">
                Extracting text and evaluating differences. Please wait...
              </p>
            </div>
            <div className="w-full h-4 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-200 ease-linear"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-center text-muted-foreground">{progress}% complete</p>
          </div>
        ) : (


          comparisonResult && (
            <div className="py-4">
              <div className="tabs">
                <div className="tabs-list">
                  <button
                    className="tabs-trigger"
                    data-state={activeTab === "summary" ? "active" : "inactive"}
                    onClick={() => setActiveTab("summary")}
                  >
                    Summary
                  </button>
                  <button
                    className="tabs-trigger"
                    data-state={activeTab === "changes" ? "active" : "inactive"}
                    onClick={() => setActiveTab("changes")}
                  >
                    Detailed Changes
                  </button>
                  <button
                    className="tabs-trigger"
                    data-state={activeTab === "recommendations" ? "active" : "inactive"}
                    onClick={() => setActiveTab("recommendations")}
                  >
                    Recommendations
                  </button>
                </div>

                <div className="tabs-content" data-state={activeTab === "summary" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                      <h3 className="mb-2 font-medium">Comparison Summary</h3>
                      <p>{comparisonResult.summary}</p>
                    </div>

                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                        <h3 className="mb-2 font-medium">{selectedReports[0]?.title}</h3>
                        <p className="text-sm text-muted-foreground">{selectedReports[0]?.date}</p>
                        <p className="text-sm text-muted-foreground">Dr. {selectedReports[0]?.doctor}</p>
                      </div>

                      <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                        <h3 className="mb-2 font-medium">{selectedReports[1]?.title}</h3>
                        <p className="text-sm text-muted-foreground">{selectedReports[1]?.date}</p>
                        <p className="text-sm text-muted-foreground">Dr. {selectedReports[1]?.doctor}</p>
                      </div>
                    </div> */}
                  </div>
                </div>

                <div className="tabs-content" data-state={activeTab === "changes" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border shadow-sm">
                      <div className="bg-muted/50 p-3">
                        <h3 className="font-medium">Parameter Changes</h3>
                      </div>
                      <div className="p-0">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="px-4 py-2 text-left font-medium">Parameter</th>
                              <th className="px-4 py-2 text-left font-medium">First Test</th>
                              <th className="px-4 py-2 text-left font-medium">Second Test</th>
                              <th className="px-4 py-2 text-left font-medium">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparisonResult.changes.map((change, index) => (
                              <tr key={index} className="border-b last:border-0">
                                <td className="px-4 py-3 font-medium">{change.parameter}</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {getStatusIcon(change.firstStatus)}
                                    <span>
                                      {change.firstValue} {change.firstUnit}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {getStatusIcon(change.secondStatus)}
                                    <span>
                                      {change.secondValue} {change.secondUnit}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {getChangeIcon(change.change, change.significance)}
                                    <span
                                      className={
                                        change.significance === "positive"
                                          ? "text-green-600"
                                          : change.significance === "negative"
                                            ? "text-red-600"
                                            : "text-blue-600"
                                      }
                                    >
                                      {change.change.charAt(0).toUpperCase() + change.change.slice(1)}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {comparisonResult.changes.map((change, index) => (
                        <div key={index} className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{change.parameter}</h4>
                            {getChangeIcon(change.change, change.significance)}
                            <span
                              className={
                                change.significance === "positive"
                                  ? "text-green-600"
                                  : change.significance === "negative"
                                    ? "text-red-600"
                                    : "text-blue-600"
                              }
                            >
                              {change.change.charAt(0).toUpperCase() + change.change.slice(1)}
                            </span>
                          </div>
                          <p className="text-sm">{change.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="tabs-content" data-state={activeTab === "recommendations" ? "active" : "inactive"}>
                  <div className="space-y-4 pt-4">
                    <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
                      <h3 className="mb-2 font-medium">Recommendations Based on Comparison</h3>
                      <ul className="space-y-2">
                        {comparisonResult.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <div className="mt-1 h-1.5 w-1.5 rounded-full bg-primary"></div>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-md bg-blue-50 p-4 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                      <div className="flex items-start">
                        <Info className="mr-2 mt-0.5 h-4 w-4" />
                        <div>
                          <h4 className="font-medium">Important Note</h4>
                          <p className="text-sm">
                            This AI comparison is for informational purposes only and does not replace professional
                            medical advice. Always consult with your healthcare provider regarding your lab results.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        <div className="dialog-footer">
          <button className="button button-outline" onClick={onClose}>
            Close
          </button>
          {/* {!analyzing && <button className="button button-default">Download Comparison</button>} */}
        </div>
      </div>
    </div>
  )
}

